import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/index.js';
import { authApi } from '../services/api/index.js';
import { zAuthLoginCreate } from '../generated/api/zod.gen.js';
import { Form, SubmitButton, TextFormField, useZodForm } from '../components/form/index.js';
import { AuthLayout } from '../components/page/AuthLayout.js';
import { redirectAfterLogin } from '../lib/login-redirect.js';

export function LoginPage() {
  const navigate = useNavigate();
  const destination = redirectAfterLogin(useLocation().search);
  const login = useAuthStore((s) => s.login);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const form = useZodForm(zAuthLoginCreate, { defaultValues: { email: '', password: '' } });

  // Already signed in — go where the person was going (an invitation link, a deep link), else home.
  useEffect(() => {
    if (isAuthenticated) navigate(destination, { replace: true });
  }, [isAuthenticated, navigate, destination]);

  const signIn = async ({ email, password }: { email: string; password: string }) => {
    const response = await authApi.login(email, password);
    if (!response.token) throw new Error('The server did not return a session. Try again.');
    if (response.user.organizationId === undefined) {
      throw new Error('This account is not part of an organization yet. Ask an administrator to add you.');
    }
    login(response.token, { ...response.user, organizationId: response.user.organizationId });
    navigate(destination, { replace: true });
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your Conflui account"
      footer={<>Conflui is invitation-only: your organization's administrator sends the invitation.</>}
    >
      <Form form={form} onSubmit={signIn}>
        <TextFormField name="email" type="email" label="Email" placeholder="you@example.com" autoComplete="email" />
        <TextFormField
          name="password"
          type="password"
          label="Password"
          placeholder="Your password"
          autoComplete="current-password"
        />
        <SubmitButton size="lg" className="w-full">
          Sign in
        </SubmitButton>
      </Form>
    </AuthLayout>
  );
}
